import { useState } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, ToastService } from "@launchmetrics/lm-ui";
import NumeralMetric from "components/LM/common/NumeralMetric";
import DateFormat from "components/LM/common/DateFormat";
import ModalSearchItem from "components/LM/SocialMediaProfile/ModalSearchItem";
import K2Api from "lib/ajax/K2Api";
import './validationSources.scss';

const k2Api = new K2Api();

const AUDIENCE_METRIC = {
	facebook: 'reach',
	instagram: 'followers',
	linkedin: 'connections',
	tiktok: 'followers',
	twitter: 'followers',
	youtube: 'subscribers',
	douyin: 'followers',
	wechat_public: 'followers',
	weibo: 'followers',
	xiaohongshu: 'followers',
	bilibili: 'followers'
};

const PROVIDER_LABELS = {
	instagram: 'IG',
	facebook: 'FB',
	tiktok: 'TK',
	twitter: 'TW',
	youtube: 'YT',
	weibo: 'WB',
	douyin: 'DY',
	xiaohongshu: 'XH',
	bilibili: 'BL',
	wechat_public: 'WC',
	linkedin: 'LI',
};

const PROVIDER_COLORS = {
	instagram: '#E1306C',
	facebook: '#4267B2',
	tiktok: '#000000',
	twitter: '#1DA1F2',
	youtube: '#FF0000',
	weibo: '#E6162D',
	douyin: '#000000',
	xiaohongshu: '#FE2C55',
	bilibili: '#00A1D6',
	wechat_public: '#7BB32E',
	linkedin: '#0A66C2',
};

function ValidationSources({ voiceId, voiceType, profiles, medias, urls, onSourcesChanged }) {
	const [activeTab, setActiveTab] = useState('social');
	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [showAddModal, setShowAddModal] = useState(false);
	const [disabled, setDisabled] = useState({});

	// Flatten profiles
	const socialProfiles = Object.entries(profiles || {}).flatMap(([type, items]) =>
		(items || []).map(item => ({ ...item, type }))
	);

	// Flatten medias
	const onlineMedias = Object.entries(medias || {}).flatMap(([type, items]) => {
		if (type === 'online') return (items || []).map(item => ({ ...item, type: 'online' }));
		return [];
	});
	const printMedias = Object.entries(medias || {}).flatMap(([type, items]) => {
		if (type === 'print') return (items || []).map(item => ({ ...item, type: 'print' }));
		return [];
	});

	const linkUrls = urls || [];

	const toggleItem = (key) => {
		setDisabled(prev => ({ ...prev, [key]: !prev[key] }));
	};

	const disabledCount = Object.values(disabled).filter(Boolean).length;

	const handleEndEditing = () => {
		setEditing(false);
		setDisabled({});
	};

	const handleRemove = async () => {
		setSaving(true);
		try {
			const promises = [];
			Object.entries(disabled).forEach(([key, isDisabled]) => {
				if (!isDisabled) return;
				const [itemType, id] = key.split('::');
				if (itemType === 'url') {
					promises.push(k2Api.delete(`/voices/${voiceId}/urls/${id}`));
				} else {
					promises.push(k2Api.delete(`/voices/${voiceId}/socialmedia/${itemType}/${id}`));
				}
			});
			await Promise.allSettled(promises);
			ToastService.show({ id: "sources-removed", title: "Sources removed", type: "success", duration: 3000 });
			setDisabled({});
			setEditing(false);
			if (onSourcesChanged) onSourcesChanged();
		} catch (e) {
			ToastService.show({ id: "sources-remove-failed", title: "Failed to remove sources", type: "error", duration: 3000 });
		} finally {
			setSaving(false);
		}
	};

	const handleAddRelations = async (type, relations) => {
		setSaving(true);
		try {
			const promises = relations.map(item => {
				if (type === 'other') {
					return k2Api.put(`/voices/${voiceId}/urls`, { url: item.url });
				}
				const profileType = item.profile_type || type;
				return k2Api.put(`/voices/${voiceId}/socialmedia/${profileType}/${item.id}`);
			});
			await Promise.allSettled(promises);
			ToastService.show({ id: "sources-added", title: "Sources added", type: "success", duration: 3000 });
			if (onSourcesChanged) onSourcesChanged();
		} catch (e) {
			ToastService.show({ id: "sources-add-failed", title: "Failed to add sources", type: "error", duration: 3000 });
		} finally {
			setSaving(false);
			setShowAddModal(false);
		}
	};

	const renderSocialItem = (profile) => {
		const key = `${profile.type}::${profile.id}`;
		const isDisabled = disabled[key];
		return (
			<div className={`vs-item ${isDisabled ? 'vs-item--disabled' : ''}`} key={key}>
				{editing && (
					<input type="checkbox" className="vs-item__checkbox" checked={!!isDisabled} onChange={() => toggleItem(key)} />
				)}
				<div
					className="vs-item__icon"
					style={{ background: PROVIDER_COLORS[profile.type] || '#6b7280' }}
				>
					{PROVIDER_LABELS[profile.type] || profile.type?.substring(0, 2).toUpperCase()}
				</div>
				<div className="vs-item__content">
					<span className="vs-item__name">{profile.name || profile.screen_name}</span>
					<span className="vs-item__meta">
						{profile.audience && <><NumeralMetric>{profile.audience}</NumeralMetric> {AUDIENCE_METRIC[profile.type]}</>}
						{profile.audience && profile.added_at && ' · '}
						{profile.added_at && <DateFormat>{profile.added_at}</DateFormat>}
					</span>
				</div>
				{profile.profile_url && (
					<a href={profile.profile_url} target="_blank" rel="noopener noreferrer" className="vs-item__link">
						↗
					</a>
				)}
			</div>
		);
	};

	const renderMediaItem = (media) => {
		const key = `${media.type}::${media.id}`;
		const isDisabled = disabled[key];
		return (
			<div className={`vs-item ${isDisabled ? 'vs-item--disabled' : ''}`} key={key}>
				{editing && (
					<input type="checkbox" className="vs-item__checkbox" checked={!!isDisabled} onChange={() => toggleItem(key)} />
				)}
				<div className="vs-item__icon vs-item__icon--media">
					{media.type === 'online' ? 'ON' : 'PR'}
				</div>
				<div className="vs-item__content">
					<span className="vs-item__name">{media.name}</span>
					<span className="vs-item__meta">
						{media.added_at && <DateFormat>{media.added_at}</DateFormat>}
					</span>
				</div>
			</div>
		);
	};

	const renderUrlItem = (url) => {
		const key = `url::${url.id}`;
		const isDisabled = disabled[key];
		return (
			<div className={`vs-item ${isDisabled ? 'vs-item--disabled' : ''}`} key={key}>
				{editing && (
					<input type="checkbox" className="vs-item__checkbox" checked={!!isDisabled} onChange={() => toggleItem(key)} />
				)}
				<div className="vs-item__icon vs-item__icon--link">🔗</div>
				<div className="vs-item__content">
					<span className="vs-item__name">{url.url || url.name}</span>
				</div>
				{url.url && (
					<a href={url.url} target="_blank" rel="noopener noreferrer" className="vs-item__link">
						↗
					</a>
				)}
			</div>
		);
	};

	const renderContent = () => {
		switch (activeTab) {
			case 'social':
				return socialProfiles.length > 0
					? socialProfiles.map(renderSocialItem)
					: <p className="vs-empty">No social profiles</p>;
			case 'online':
				return onlineMedias.length > 0
					? onlineMedias.map(renderMediaItem)
					: <p className="vs-empty">No online media</p>;
			case 'print':
				return printMedias.length > 0
					? printMedias.map(renderMediaItem)
					: <p className="vs-empty">No print media</p>;
			case 'links':
				return linkUrls.length > 0
					? linkUrls.map(renderUrlItem)
					: <p className="vs-empty">No links</p>;
			default:
				return null;
		}
	};

	return (
		<div className="vs-sources">
			<div className="vs-sources__actions">
				{editing ? (
					<>
						{disabledCount > 0 && (
							<button type="button" className="vs-sources__btn vs-sources__btn--remove" onClick={handleRemove} disabled={saving}>
								🗑 Remove {disabledCount} selected
							</button>
						)}
						<button type="button" className="vs-sources__btn vs-sources__btn--end" onClick={handleEndEditing}>
							End editing
						</button>
						<button type="button" className="vs-sources__btn vs-sources__btn--add" onClick={() => setShowAddModal(true)}>
							+ Add Source
						</button>
					</>
				) : (
					<button type="button" className="vs-sources__btn vs-sources__btn--edit" onClick={() => setEditing(true)}>
						Edit Sources
					</button>
				)}
			</div>
			<Tabs value={activeTab} onSelected={(val) => setActiveTab(val)} styleProps={{ fontSize: '13px' }}>
				<Tab value="social" size="small" label={<><span className="vs-tab-label">Social</span> <span className="vs-tab-count">{socialProfiles.length}</span></>} />
				<Tab value="online" size="small" label={<><span className="vs-tab-label">Online</span> <span className="vs-tab-count">{onlineMedias.length}</span></>} />
				<Tab value="print" size="small" label={<><span className="vs-tab-label">Print</span> <span className="vs-tab-count">{printMedias.length}</span></>} />
				<Tab value="links" size="small" label={<><span className="vs-tab-label">Links</span> <span className="vs-tab-count">{linkUrls.length}</span></>} />
			</Tabs>
			<div className="vs-list">
				{renderContent()}
			</div>

			<ModalSearchItem
				show={showAddModal}
				onClose={() => setShowAddModal(false)}
				onAdd={handleAddRelations}
				voiceId={voiceId}
				voiceType={voiceType}
			/>
		</div>
	);
}

ValidationSources.propTypes = {
	voiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	voiceType: PropTypes.string,
	profiles: PropTypes.object,
	medias: PropTypes.object,
	urls: PropTypes.array,
	onSourcesChanged: PropTypes.func,
};

export default ValidationSources;
